import type {
  ApiError,
  ChannexQuarantinedRevision,
  ChannexRevisionHoldState,
  ChannexRevisionReplayResult,
} from '@/shared/api';
import { quarantineReason } from './quarantineLabels';

const STATE_LABEL: Record<ChannexRevisionHoldState, string> = {
  AwaitingRoute: 'Wartet auf Zuordnung',
  Quarantined: 'In Quarantäne',
};

export function holdStateLabel(state: ChannexRevisionHoldState): string {
  return STATE_LABEL[state] ?? state;
}

/**
 * Warum der Server „Erneut verarbeiten" nicht annimmt — dieselben Codes liefert die Liste
 * (`replayRefusal`) und die Ablehnung der Aktion. Ein unbekannter Code bleibt sichtbar.
 */
const REFUSAL: Record<string, string> = {
  revision_hold_cause_definitive:
    'Der Fehler steckt in der Revision selbst — eine erneute Verarbeitung ergäbe dasselbe. Die Buchung muss beim Kanal geprüft werden.',
  revision_hold_processing_failed:
    'Erneut verarbeiten hilft hier nicht — der Support muss die Ursache zuerst beheben.',
  revision_hold_awaiting_route:
    'Die Revision wartet noch und wird automatisch erneut versucht, sobald ihre Zuordnung steht.',
  revision_hold_object_unknown:
    'Die Revision ist gescheitert, bevor ein Objekt feststand — es gibt nichts, wogegen sie erneut zugeordnet werden könnte.',
  revision_hold_payload_purged: 'Der gespeicherte Stand dieser Revision ist bereits gelöscht.',
  revision_hold_payload_unreadable:
    'Der gespeicherte Stand dieser Revision lässt sich nicht entschlüsseln — bitte den Support einschalten.',
  revision_hold_target_changed:
    'Die Revision hat sich während der Anfrage geändert. Bitte die Liste neu laden.',
  replay_finance_journal_missing:
    'Zu dieser Revision fehlt das Finanzjournal; ohne es entstünde eine Buchung ohne Preis und Rechnung. Der Support kann es nachladen.',
};

export function replayRefusalText(code: string | null): string {
  if (!code) return 'Erneut verarbeiten ist hier nicht möglich.';
  // Unbekannt: der Code bleibt für den Support sichtbar, aber in einem Satz statt nackt.
  return REFUSAL[code] ?? `Erneut verarbeiten lehnt der Server ab (Code ${code}).`;
}

/** Der Satz zum Ergebnis — erst nach der Antwort des Servers, nie vorher. */
export function replayOutcomeText(result: ChannexRevisionReplayResult, receiptId: string): string {
  switch (result.outcome) {
    case 'Applied':
      return 'Angewendet — die Buchung ist angelegt und steht in Kalender und Buchungsliste.';
    case 'Superseded':
      return 'Überholt — ein neuerer Stand dieser Buchung ist bereits angewendet.';
    default:
      return result.code === 'replay_failed'
        ? `Die Verarbeitung ist abgebrochen; es entstand keine Buchung. Wiederholt sich das, bitte dem Support melden, Kennung ${receiptId}.`
        : `Weiterhin nicht zuordenbar: ${quarantineReason(result.code)}`;
  }
}

/**
 * Ohne Antwort ist offen, ob der Server verarbeitet hat; der Satz darf dann nicht „nein" sagen.
 * Eine Ablehnung nennt ihren Grund aus derselben Zuordnung wie die Liste, jeder andere Fehler
 * den des Servers — die Liste lädt danach neu und zeigt, was wirklich gilt.
 */
export function replayFailureText(problem: ApiError): string {
  if (problem.status === 0) {
    return 'Keine Antwort vom Server – ob verarbeitet wurde, ist offen. Die Liste wird neu geladen.';
  }
  if (problem.status === 409) return replayRefusalText(problem.title);
  if (problem.status === 404) return 'Diese Revision gibt es in diesem Workspace nicht mehr.';
  if (problem.status === 403) return 'Dafür fehlt Ihnen die Berechtigung.';
  const reason = problem.detail ? `: ${problem.detail}` : '';
  return `Erneut verarbeiten ist fehlgeschlagen${reason}. Die Liste zeigt den aktuellen Stand.`;
}

/** Eine übernommene Zeile, festgehalten mit ihrem Platz in der Liste zum Zeitpunkt der Aktion. */
export interface SettledHold {
  row: ChannexQuarantinedRevision;
  index: number;
}

/**
 * Eine gerade übernommene Revision liefert der Server nicht mehr — ihre Bestätigung bleibt
 * trotzdem an ihrem Platz stehen, während die Liste neu lädt und Wiederwartende dazukommen.
 */
export function withSettledRows(
  rows: readonly ChannexQuarantinedRevision[],
  settled: readonly SettledHold[],
): ChannexQuarantinedRevision[] {
  const result = [...rows];
  for (const entry of [...settled].sort((a, b) => a.index - b.index)) {
    if (result.some((row) => row.id === entry.row.id)) continue;
    result.splice(Math.min(entry.index, result.length), 0, entry.row);
  }
  return result;
}

/** Kanalnamen wie im Buchungskopf; ein unbekannter Kanal bleibt ehrlich „Sonstige OTA". */
export function channelLabel(channel: string): string {
  if (channel === 'BookingCom') return 'Booking.com';
  if (channel === 'Airbnb') return 'Airbnb';
  return 'Sonstige OTA';
}
